import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import NcInputNumber from "components/NcInputNumber/NcInputNumber";
import { FC } from "react";
import ButtonSubmit from "./ButtonSubmit";
import { GuestsObject } from "components/HeroSearchForm2Mobile/GuestsInput";
import { PathName } from "routers/types";
import Select from "shared/Select/Select";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { addLetterSofPlural } from "utils/string";

export const DEFAULT_ROOM_DATA = {
  id: Math.random(),
  guestAdults: 2,
  guestChildren: [],
};

export interface IRoom {
  guestAdults: number;
  guestChildren: number[];
  id?: number;
}

export interface GuestsInputProps {
  defaultValue: GuestsObject;
  rooms?: IRoom[];
  onChange?: (data: IRoom[]) => void; 
  fieldClassName?: string;
  className?: string;
  buttonSubmitHref?: PathName | string;
  hasButtonSubmit?: boolean;
  defaultFocus?: boolean;
  maxChildAge?: number;
}

const GuestsInput: FC<GuestsInputProps> = ({
  rooms,
  defaultValue,
  onChange,
  fieldClassName = "[ nc-hero-field-padding ]",
  className = "[ nc-flex-1 ]",
  buttonSubmitHref = "/hotels",
  hasButtonSubmit = true,
  maxChildAge = 12, 
}) => {
  const [data, setData] = useState<IRoom[]>(rooms || [DEFAULT_ROOM_DATA]);

  // Centralized state update with notification
  const setDataAndNotify = (newData: IRoom[]) => {
    setData(newData);
    onChange?.(newData);
  };

  // Parse URL parameters with useMemo for performance
  const parsedRooms = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const rooms: IRoom[] = [];

    const roomCount = parseInt(params.get("rooms") ?? "0");
    if (roomCount <= 0 || isNaN(roomCount)) {
      console.warn("Invalid or missing 'rooms' parameter; using default room data");
      return [DEFAULT_ROOM_DATA];
    }

    for (let i = 1; i <= roomCount; i++) {
      const guestAdults = parseInt(params.get(`adults${i}`) ?? "0");
      const childrenStr = params.get(`children${i}`) ?? "";
      const guestChildren = childrenStr
        .split(",")
        .map((age) => parseInt(age))
        .filter(Number.isFinite);

      if (guestAdults < 1 || isNaN(guestAdults)) {
        console.warn(`Invalid 'adults${i}' parameter; skipping room ${i}`);
        continue;
      }

      rooms.push({
        guestAdults,
        guestChildren,
        id: Math.random(),
      });
    }

    return rooms.length > 0 ? rooms : [DEFAULT_ROOM_DATA];
  }, [window.location.search]);

  // Sync state with URL parameters
  useEffect(() => {
    setDataAndNotify(parsedRooms);

    const updateRoomsFromUrl = () => {
      setDataAndNotify(parsedRooms);
    };

    window.addEventListener("popstate", updateRoomsFromUrl);
    return () => {
      window.removeEventListener("popstate", updateRoomsFromUrl);
    };
  }, [parsedRooms]);

  // Handle changes to adults, children count, or child ages
  const handleChangeData = (
    value: number,
    type: keyof GuestsObject | "guestChildrenAge",
    roomIndex: number,
    childIndex?: number
  ) => {
    // Validate input value
    if (value < 0 || isNaN(value)) {
      console.warn(`Invalid value ${value} for ${type} in room ${roomIndex + 1}`);
      return;
    }

    setDataAndNotify(
      data.map((room, index) => {
        if (roomIndex === index) {
          if (type === "guestAdults") {
            return { ...room, guestAdults: Math.min(value, 5) }; // Enforce max
          }
          if (type === "guestChildrenAge") {
            return {
              ...room,
              guestChildren: room.guestChildren.map((age, i) =>
                i === childIndex ? Math.min(value, maxChildAge) : age
              ),
            };
          }
          return {
            ...room,
            guestChildren:
              value > room.guestChildren.length
                ? [...room.guestChildren, ...Array(value - room.guestChildren.length).fill(0)]
                : room.guestChildren.slice(0, value),
          };
        }
        return room;
      })
    );
  };

  // Calculate total guests for display
  const totalGuests = data.reduce(
    (acc, val) => {
      acc.total = acc.total + val.guestAdults + val.guestChildren.length;
      acc.totalAdults = acc.totalAdults + val.guestAdults;
      acc.totalChildren = acc.totalChildren + val.guestChildren.length;
      return acc;
    },
    {
      total: 0,
      totalAdults: 0,
      totalChildren: 0,
    }
  );

  return (
    <Popover className={`flex relative ${className}`}>
      {({ open, close }) => (
        <>
          <div
            className={`flex-1 flex items-center focus:outline-none cursor-pointer ${
              open ? "nc-hero-field-focused" : ""
            }`}
          >
            <Popover.Button
              className={`flex-1 flex text-left items-center ${fieldClassName} space-x-2 `}            >
              <div className="text-neutral-300 dark:text-neutral-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="nc-icon-field"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              </div>
              <div className="flex-grow">
                <span className="block xl:text-base font-semibold">
                  {data.length} Chambre{addLetterSofPlural(data.length)}
                </span>
                <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
                  {totalGuests.totalAdults
                    ? `${totalGuests.totalAdults} Adulte${addLetterSofPlural(
                        totalGuests.totalAdults
                      )}`
                    : ""}{" "}
                  {totalGuests.totalChildren
                    ? `, ${
                        totalGuests.totalChildren
                      } Enfant${addLetterSofPlural(totalGuests.totalChildren)}`
                    : ""}
                </span>
              </div>
            </Popover.Button>

            {/* BUTTON SUBMIT OF FORM */}
            {hasButtonSubmit && (
              <div className="pr-2 xl:pr-4">
                <ButtonSubmit href={buttonSubmitHref as PathName} />
              </div>
            )}
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-50 w-max right-0 sm:min-w-[340px] bg-white dark:bg-neutral-800 top-full mt-3 py-5 sm:py-6 px-4 sm:px-8  shadow-xl">
              {data.length === 0 && (
                <div className="text-sm text-red-500 mb-4">
                  No rooms available. Please try again.
                </div>
              )}
              <div className="flex">
                {data.map((room, index) => (
                  <div
                    key={room.id ? room.id + index : index}
                    className={`max-h-80 overflow-y-auto ${
                      data.length !== 1 && data.length !== index + 1
                        ? "mr-5 pr-5 border-r border-solid"
                        : ""
                    }`}
                  >
                    <div className="flex items-center mb-2 pb-2">
                      <div className="mr-3 text-sm">Chambre {index + 1}</div>
                      {data.length !== 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setDataAndNotify(data.filter((_, i) => i !== index))
                          }
                          aria-label={`Remove room ${index + 1}`}
                        >
                          <MinusCircleIcon className="w-7 h-7" />
                        </button>
                      )}
                    </div>
                    <NcInputNumber
                      className="w-full mb-6"
                      defaultValue={room.guestAdults}
                      onChange={(value) =>
                        handleChangeData(value, "guestAdults", index)
                      }
                      max={5}
                      min={1}
                      label="Adultes"
                      desc=""
                    />
                    <NcInputNumber
                      className="w-full mb-6"
                      defaultValue={room.guestChildren.length}
                      onChange={(value) =>
                        handleChangeData(value, "guestChildren", index)
                      }
                      max={4}
                      label="Enfants"
                      desc={`${maxChildAge} an${addLetterSofPlural(maxChildAge)}`}
                    />
                    {room.guestChildren.map((child, i) => (
                      <div className="py-2" key={i}>
                        <div className="">Âge Enfant {i + 1}</div>
                        <Select
                          onChange={(e) =>
                            handleChangeData(
                              Number(e.target.value),
                              "guestChildrenAge",
                              index,
                              i
                            )
                          }
                          value={child}
                        >
                          {Array.from({ length: maxChildAge + 1 }, () => {}).map(
                            (_, age) => (
                              <option value={age} key={age}>
                                {age || "0-1"} an{addLetterSofPlural(age)}
                              </option>
                            )
                          )}
                        </Select>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {data.length !== 4 && (
                <div className="flex justify-end mt-3 pt-3">
                  <button
                    type="button"
                    className="flex items-center"
                    onClick={() =>
                      setDataAndNotify(data.concat({ ...DEFAULT_ROOM_DATA }))
                    }
                    aria-label="Add a room"
                  >
                    <PlusCircleIcon className="w-6 h-6 mr-2" />
                    <div className="text-sm">Ajouter une Chambre</div>
                  </button>
                </div>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default GuestsInput;